(() => {
  if (!window.HTMLDialogElement?.prototype.showModal || customElements.get('dac-beginner-guide')) return;
  customElements.define('dac-beginner-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.viewerReady) return;
      const guide = this;
      const controller = new AbortController();
      const on = (node, name, fn) => node.addEventListener(name, fn, {signal: controller.signal});
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      let active, opener, saved, drag, closeTimer, outsideDown;
      const setOverflow = () => {
        if (active) active.toggleAttribute('data-scrollable', active.scrollHeight > active.clientHeight + 2);
      };
      const resize = () => {
        if (active && saved.width !== innerWidth) {
          const anchor = Math.min(saved.anchor, Math.max(120, innerHeight - 180));
          saved.y = Math.max(0, Math.min(document.body.scrollHeight - innerHeight, saved.y + opener.getBoundingClientRect().top - anchor));
          saved.width = innerWidth;
          saved.anchor = anchor;
          document.body.style.setProperty('--dac-guide-page-y', `${-saved.y}px`);
          resetDrag();
        }
        setOverflow();
      };
      const resetDrag = () => {
        const current = drag;
        drag = null;
        if (!active) return;
        if (current && active.hasPointerCapture(current.id)) active.releasePointerCapture(current.id);
        active.classList.remove('is-dragging');
        active.classList.add('is-settling');
        active.style.removeProperty('--guide-drag-y');
        active.style.removeProperty('--guide-shade');
      };
      const cleanup = dialog => {
        if (active !== dialog) return;
        clearTimeout(closeTimer);
        resetDrag();
        active = null;
        dialog.classList.remove('is-dragging', 'is-settling', 'is-closing');
        dialog.removeAttribute('data-scrollable');
        document.documentElement.classList.remove('dac-guide-viewer-lock');
        document.body.classList.remove('dac-guide-viewer-lock');
        document.body.style.removeProperty('--dac-guide-page-y');
        window.removeEventListener('resize', resize);
        window.scrollTo({left: saved.x, top: saved.y, behavior: 'instant'});
        if (opener?.isConnected) opener.focus({preventScroll: true});
        opener = null;
      };
      const close = (swipe = false) => {
        if (!active || (swipe && active.classList.contains('is-closing'))) return;
        const dialog = active;
        const finish = () => { dialog.close(); cleanup(dialog); };
        if (!swipe || reduced.matches) return finish();
        drag = null;
        dialog.classList.remove('is-dragging', 'is-settling');
        dialog.classList.add('is-closing');
        closeTimer = setTimeout(finish, 180);
      };
      const open = (button, keyboard) => {
        if (active) return;
        const dialog = guide.querySelector(`[id="${CSS.escape(button.dataset.guideOpen)}"]`);
        if (!dialog) return;
        opener = button;
        saved = {x: scrollX, y: scrollY, anchor: button.getBoundingClientRect().top, width: innerWidth};
        guide.dataset.viewerInput = keyboard ? 'keyboard' : 'pointer';
        active = dialog;
        document.body.style.setProperty('--dac-guide-page-y', `${-saved.y}px`);
        document.documentElement.classList.add('dac-guide-viewer-lock');
        document.body.classList.add('dac-guide-viewer-lock');
        dialog.showModal();
        dialog.scrollTop = 0;
        dialog.querySelector('[data-guide-close]').focus({preventScroll: true});
        const img = dialog.querySelector('img'), status = dialog.querySelector('[role="status"]');
        status.hidden = img.complete && img.naturalWidth > 0;
        status.textContent = img.dataset.failed ? status.dataset.error : status.dataset.loading;
        setOverflow();
        window.addEventListener('resize', resize, {passive: true});
      };
      on(guide, 'click', event => {
        const button = event.target.closest('[data-guide-open]');
        if (button) { event.preventDefault(); open(button, event.detail === 0); }
      });
      on(guide, 'keydown', event => {
        guide.dataset.viewerInput = 'keyboard';
        if (!active || event.key !== 'Tab') return;
        const controls = [...active.querySelectorAll('button:not([disabled]),a[href]')];
        event.preventDefault();
        const index = controls.indexOf(document.activeElement);
        controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length]?.focus({preventScroll: true});
      });
      for (const dialog of guide.querySelectorAll('.dac-guide__zoom-view')) {
        const img = dialog.querySelector('img'), status = dialog.querySelector('[role="status"]');
        status.dataset.loading = status.textContent;
        on(img, 'load', () => { status.hidden = true; delete img.dataset.failed; setOverflow(); });
        on(img, 'error', () => { img.dataset.failed = 'true'; status.textContent = status.dataset.error; status.hidden = false; setOverflow(); });
        on(dialog, 'cancel', event => { event.preventDefault(); close(); });
        on(dialog, 'close', () => cleanup(dialog));
        const outside = event => {
          const r = dialog.getBoundingClientRect();
          return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
        };
        on(dialog, 'click', event => {
          if (event.target.closest('[data-guide-close]') || (event.target === dialog && outsideDown && outside(event))) close();
          outsideDown = false;
        });
        on(dialog, 'pointerdown', event => {
          guide.dataset.viewerInput = 'pointer';
          outsideDown = event.target === dialog && outside(event);
          if (!event.isPrimary) return resetDrag();
          if (dialog !== active || dialog.classList.contains('is-closing') || !['touch', 'pen'].includes(event.pointerType)) return;
          if (event.target.closest('button,a') || !event.target.closest('.dac-guide__zoom-header,.dac-guide__zoom-handle,.dac-guide__zoom-media')) return;
          if (event.target.closest('.dac-guide__zoom-media') && dialog.hasAttribute('data-scrollable')) return;
          drag = {id: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp, dy: 0, moving: false};
          dialog.setPointerCapture(event.pointerId);
        });
        on(dialog, 'pointermove', event => {
          if (!drag || event.pointerId !== drag.id) return;
          const dx = Math.abs(event.clientX - drag.x), dy = event.clientY - drag.y;
          if (!drag.moving && (dy < -12 || (dx > 12 && dx > Math.abs(dy)))) return resetDrag();
          if (!drag.moving && (dy < 10 || dy < dx * 1.25)) return;
          drag.moving = true;
          drag.dy = Math.max(0, dy);
          dialog.classList.remove('is-settling');
          dialog.classList.add('is-dragging');
          if (!reduced.matches) {
            dialog.style.setProperty('--guide-drag-y', `${drag.dy}px`);
            dialog.style.setProperty('--guide-shade', Math.max(.25, 1 - drag.dy / (innerHeight * .8)));
          }
          if (event.cancelable) event.preventDefault();
        });
        on(dialog, 'pointerup', event => {
          if (!drag || event.pointerId !== drag.id) return;
          const current = drag;
          const dismiss = current.moving && (current.dy >= 96 || (current.dy >= 48 && current.dy / Math.max(1, event.timeStamp - current.time) > .5));
          drag = null;
          if (dialog.hasPointerCapture(event.pointerId)) dialog.releasePointerCapture(event.pointerId);
          if (dismiss) close(true); else resetDrag();
        });
        on(dialog, 'pointercancel', resetDrag);
        on(dialog, 'lostpointercapture', () => { if (drag) resetDrag(); });
      }
      on(window, 'pagehide', () => close());
      this.viewerCleanup = () => {
        if (active) { const dialog = active; clearTimeout(closeTimer); dialog.close(); cleanup(dialog); }
        controller.abort();
        delete guide.dataset.viewerReady;
      };
      guide.dataset.viewerReady = 'true';
    }
    disconnectedCallback() { this.viewerCleanup?.(); }
  });
})();
